import { tenantTerminologyService } from '@/services/tenant-terminology.service';

/**
 * Backs the admin-tenant terminology editor. Loads the editor snapshot (registry, locales, form
 * keys, current customizations), proxies live previews, and saves/resets terms. After a save or
 * reset it dispatches `terminology/reload` (root) so the running app reflects the change live.
 */

const initialState = () => ({
  entities: [],
  locales: [],
  formKeys: [],
  terms: [], // [{ entityId, locale, singular, plural, gender, startsWithVowelSound, forms, overrides }]
  status: {
    error: null,
    pending: false,
    saving: false,
  },
});

const state = initialState();

function sameTerm(a, entityId, locale) {
  return a.entityId === entityId && a.locale === locale;
}

const actions = {
  getSnapshot({ commit }) {
    commit('GET_SNAPSHOT');
    return tenantTerminologyService.getAdminSnapshot().then(
      (snapshot) => commit('GET_SNAPSHOT_SUCCESS', snapshot),
      (error) => {
        commit('GET_SNAPSHOT_ERROR', error);
        throw error;
      },
    );
  },
  // Stateless live preview; returns the response so the component can render the previewed forms.
  preview(_context, input) {
    return tenantTerminologyService.preview(input);
  },
  saveTerm({ commit, dispatch }, { entityId, locale, input }) {
    commit('SAVING');
    return tenantTerminologyService.saveTerm(entityId, locale, input).then(
      (term) => {
        commit('SAVE_SUCCESS', term);
        dispatch('terminology/reload', null, { root: true });
        return term;
      },
      (error) => {
        commit('SAVE_ERROR', error);
        throw error;
      },
    );
  },
  resetTerm({ commit, dispatch }, { entityId, locale }) {
    commit('SAVING');
    return tenantTerminologyService.resetTerm(entityId, locale).then(
      () => {
        commit('RESET_SUCCESS', { entityId, locale });
        dispatch('terminology/reload', null, { root: true });
      },
      (error) => {
        commit('SAVE_ERROR', error);
        throw error;
      },
    );
  },
};

const mutations = {
  GET_SNAPSHOT(state) {
    state.status = { ...state.status, pending: true, error: null };
  },
  GET_SNAPSHOT_SUCCESS(state, snapshot) {
    state.status = { ...state.status, pending: false };
    state.entities = snapshot.entities || [];
    state.locales = snapshot.locales || [];
    state.formKeys = snapshot.formKeys || [];
    state.terms = snapshot.terms || [];
  },
  GET_SNAPSHOT_ERROR(state, error) {
    state.status = { ...state.status, pending: false, error };
  },
  SAVING(state) {
    state.status = { ...state.status, saving: true, error: null };
  },
  SAVE_SUCCESS(state, term) {
    state.status = { ...state.status, saving: false };
    const next = state.terms.filter((t) => !sameTerm(t, term.entityId, term.locale));
    next.push(term);
    state.terms = next;
  },
  RESET_SUCCESS(state, { entityId, locale }) {
    state.status = { ...state.status, saving: false };
    state.terms = state.terms.filter((t) => !sameTerm(t, entityId, locale));
  },
  SAVE_ERROR(state, error) {
    state.status = { ...state.status, saving: false, error };
  },
};

const getters = {
  entities: (state) => state.entities,
  locales: (state) => state.locales,
  formKeys: (state) => state.formKeys,
  terms: (state) => state.terms,
  termFor: (state) => (entityId, locale) => state.terms.find((t) => sameTerm(t, entityId, locale)) || null,
  pending: (state) => state.status.pending,
  saving: (state) => state.status.saving,
  error: (state) => state.status.error,
};

export const adminTerminology = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
